import type { ControlElement, JsonSchema, UISchemaElement } from '@jsonforms/core';
import { getEnvelopeValidationErrors, type ValidationError } from './validationErrors';
import { getEnvelopeWarnings, type RuleWarning } from './ruleWarnings';

/**
 * Bulk edit from the list view (docs/15-designspecs/Bulk-Edit-And-Actions.md): which fields the dialog
 * offers and how they are edited, the request it sends and what the outcome per row means. The React
 * dialog (components/bulkEdit/BulkEditDialog.tsx) renders; everything it decides is here.
 */

/** One field the entity offers for bulk edit, as generated from the DSL beside the server's allow-list. */
export interface BulkEditFieldSpec {
  /** The field's name in the request and in field restrictions. */
  name: string;
  /** The update-DTO properties it writes (camelCase): one, or a principal's type, id and qualifier. */
  properties: string[];
  /** True when the field cannot be cleared. */
  required: boolean;
  /** Fields that must be in the same edit for this one to be offered: a dependent lookup's sources (D15). */
  dependsOn: string[];
}

/** What a generated list view hands the dialog. */
export interface BulkEditConfig {
  /** POST endpoint, e.g. `/api/sales/order/bulk-edit`. */
  endpoint: string;
  fields: BulkEditFieldSpec[];
  /** The entity's update form schema and UI schema: the dialog edits each field with its form control. */
  schema: JsonSchema;
  uischema: UISchemaElement;
  /** True when the entity allows user-defined fields: the dialog offers them too, as the form shows them. */
  udfs?: boolean;
}

/** How a request names a user-defined field (BulkEditUdf on the server): never the name of one of the entity's fields. */
export const UDF_FIELD_PREFIX = 'extraProperties.';

/**
 * The config with the entity's user-defined fields added, from the form's schemas once the UDFs are merged into
 * them (useEnhancedJsonForms): each UDF the form shows becomes a field, required as the form requires it. A
 * read-only one is left out — the server would refuse the change.
 */
export function withUdfFields(config: BulkEditConfig, enhancedSchema: JsonSchema, enhancedUiSchema: UISchemaElement): BulkEditConfig {
  const base = new Set(Object.keys(config.schema.properties ?? {}));
  const required = new Set(enhancedSchema.required ?? []);
  const readOnly = new Set(controlsOf(enhancedUiSchema)
    .filter((control) => control.options?.readonly === true)
    .map((control) => control.scope.replace('#/properties/', '')));
  const udfs = Object.keys(enhancedSchema.properties ?? {})
    .filter((name) => !base.has(name) && !readOnly.has(name))
    .map((name): BulkEditFieldSpec => ({
      name: `${UDF_FIELD_PREFIX}${name}`,
      properties: [name],
      required: required.has(name),
      dependsOn: [],
    }));
  return { ...config, schema: enhancedSchema, uischema: enhancedUiSchema, fields: [...config.fields, ...udfs] };
}

/** Set on every row, fill only where the field is empty (D17), or clear. */
export type BulkEditOperation = 'Set' | 'FillEmpty' | 'Clear';

/** One field the user added to the dialog. */
export interface BulkEditEntry {
  field: string;
  operation: BulkEditOperation;
}

/** A selected row and the stamp the grid loaded it with (D7). */
export interface BulkRowRef {
  id: string;
  concurrencyStamp?: string;
}

/** The server's outcome for one row (§6.3). */
export interface BulkRowOutcome {
  id: string;
  label: string;
  concurrencyStamp?: string | null;
  outcome: 'Applied' | 'Refused' | 'Warned' | 'Skipped' | 'Unchanged';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the ABP error envelope, as the server returns it
  error?: any;
}

export interface BulkOperationResult {
  results: BulkRowOutcome[];
}

/** One advisory rule the warned rows raised, and which rows (D14). */
export interface BulkWarningGroup {
  warning: RuleWarning;
  rows: BulkRowRef[];
}

/** What a run came to, in the terms the list view reports it. */
export interface BulkEditSummary {
  total: number;
  applied: number;
  /** Rows left as they were: every field filled only where empty already had a value there (D17). */
  unchanged: number;
  /** Each refused row's errors, the row named first (`ORD-000012: …`), as commandPerRow.ts names them. */
  refusals: ValidationError[];
  /** The warned rows by rule, for "Acknowledge and run these N". */
  warnings: BulkWarningGroup[];
  /** Every warned row once, with the stamp it was selected with: nothing was written to it. */
  warnedRows: BulkRowRef[];
}

/** True when the control edits one of the properties, itself or a part of it (a value object's street). */
function controlEdits(control: ControlElement, properties: string[]): boolean {
  return properties.some((p) => control.scope === `#/properties/${p}` || control.scope.startsWith(`#/properties/${p}/`));
}

/** Every Control of the UI schema, first occurrence of each scope only (a form may place one twice, on two tabs). */
function controlsOf(element: UISchemaElement, seen = new Set<string>()): ControlElement[] {
  const found: ControlElement[] = [];
  if (element.type === 'Control' && typeof (element as ControlElement).scope === 'string') {
    const control = element as ControlElement;
    if (!seen.has(control.scope)) {
      seen.add(control.scope);
      found.push(control);
    }
  }
  for (const child of (element as { elements?: UISchemaElement[] }).elements ?? []) {
    found.push(...controlsOf(child, seen));
  }
  return found;
}

/**
 * The editor for one field: its controls from the update form — same lookups, enum pickers and value-object
 * blocks — over a schema of just its properties. The controls lose their visibility rules and grid widths,
 * which belong to the form's layout, not to the value. Null when the form has no control for it: the dialog
 * cannot edit what the form cannot.
 */
export function editorFor(
  spec: BulkEditFieldSpec,
  schema: JsonSchema,
  uischema: UISchemaElement,
): { schema: JsonSchema; uischema: UISchemaElement } | null {
  const controls = controlsOf(uischema).filter((c) => controlEdits(c, spec.properties));
  if (controls.length === 0) return null;
  const all = (schema.properties ?? {}) as Record<string, JsonSchema>;
  const properties = Object.fromEntries(spec.properties.filter((p) => all[p] !== undefined).map((p) => [p, all[p]]));
  return {
    schema: { ...schema, properties, required: [] } as JsonSchema,
    uischema: {
      type: 'VerticalLayout',
      elements: controls.map(asEditorControl),
    } as UISchemaElement,
  };
}

/** The form's control without what belongs to the form's layout: its visibility rule and grid width. */
function asEditorControl(control: ControlElement): ControlElement {
  const editor: ControlElement = { ...control };
  delete editor.rule;
  if (control.options) {
    const options = { ...control.options };
    delete options.gridColumn;
    delete options.width;
    editor.options = options;
  }
  return editor;
}

/**
 * The fields "+ Add field" offers now: not yet added, editable by this user (a field restriction takes it
 * away, as the server would refuse it — matched ignoring case, as the server matches it), with a control in the
 * form, and a dependent lookup only once its sources are in the edit (D15).
 */
export function addableFields(
  config: BulkEditConfig,
  entries: readonly BulkEditEntry[],
  restrictions: ReadonlyMap<string, string>,
): BulkEditFieldSpec[] {
  const added = new Set(entries.map((e) => e.field));
  const restricted = new Set([...restrictions.keys()].map((name) => name.toLowerCase()));
  return config.fields.filter((spec) =>
    !added.has(spec.name)
    && !restricted.has(spec.name.toLowerCase())
    && spec.properties.every((p) => !restricted.has(p.toLowerCase()))
    && spec.dependsOn.every((source) => added.has(source))
    && editorFor(spec, config.schema, config.uischema) !== null);
}

/** Removing a field removes the fields that were offered only because of it (D15). */
export function withoutField(entries: readonly BulkEditEntry[], field: string, config: BulkEditConfig): BulkEditEntry[] {
  const removed = new Set([field]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const spec of config.fields) {
      if (!removed.has(spec.name) && spec.dependsOn.some((source) => removed.has(source))) {
        removed.add(spec.name);
        changed = true;
      }
    }
  }
  return entries.filter((e) => !removed.has(e.field));
}

/** The request body (§6.1): a Set or a FillEmpty carries the values of the field's properties, a Clear none. */
export function bulkEditRequest(
  rows: readonly BulkRowRef[],
  entries: readonly BulkEditEntry[],
  values: Record<string, unknown>,
  config: BulkEditConfig,
  acknowledgedWarnings: readonly string[] = [],
) {
  return {
    selection: { rows: rows.map((r) => ({ id: r.id, concurrencyStamp: r.concurrencyStamp })) },
    fields: entries.map((entry) => {
      const spec = config.fields.find((f) => f.name === entry.field)!;
      return entry.operation === 'Clear'
        ? { field: entry.field, operation: 'Clear' as const }
        : {
            field: entry.field,
            operation: entry.operation,
            values: Object.fromEntries(spec.properties.map((p) => [p, values[p] ?? null])),
          };
    }),
    acknowledgedWarnings: [...acknowledgedWarnings],
  };
}

/** What the run came to: counts, each refused row's errors named by the row, and the warned rows by rule. */
export function summarizeBulkEdit(result: BulkOperationResult): BulkEditSummary {
  const refusals: ValidationError[] = [];
  const warnings = new Map<string, BulkWarningGroup>();
  const warnedRows: BulkRowRef[] = [];
  for (const row of result.results) {
    if (row.outcome === 'Refused') refusals.push(...refusalOf(row));
    if (row.outcome === 'Warned') warnedRows.push(fileWarnedRow(row, warnings));
  }
  const count = (outcome: BulkRowOutcome['outcome']) => result.results.filter((r) => r.outcome === outcome).length;
  return {
    total: result.results.length,
    applied: count('Applied'),
    unchanged: count('Unchanged'),
    refusals,
    warnings: [...warnings.values()],
    warnedRows,
  };
}

/** A warned row, filed under every rule it raised; returned with the stamp it was selected with. */
function fileWarnedRow(row: BulkRowOutcome, warnings: Map<string, BulkWarningGroup>): BulkRowRef {
  const ref = { id: row.id, concurrencyStamp: row.concurrencyStamp ?? undefined };
  for (const warning of getEnvelopeWarnings(row.error) ?? []) {
    const group = warnings.get(warning.ruleKey) ?? { warning, rows: [] };
    group.rows.push(ref);
    warnings.set(warning.ruleKey, group);
  }
  return ref;
}

/**
 * True when some row was neither updated nor already as asked: refused, or warned and left without running because
 * the dialog was closed on its warnings (D14).
 */
export function bulkRunIsPartial(summary: BulkEditSummary): boolean {
  return summary.applied + summary.unchanged < summary.total;
}

/**
 * A re-run's summary on top of the run before it (D14): the rows that run applied or refused still count, and
 * what is left warned is what the re-run says.
 */
export function mergeBulkEditSummaries(earlier: BulkEditSummary | null, latest: BulkEditSummary): BulkEditSummary {
  if (!earlier) return latest;
  return {
    total: earlier.total,
    applied: earlier.applied + latest.applied,
    unchanged: earlier.unchanged + latest.unchanged,
    refusals: [...earlier.refusals, ...latest.refusals],
    warnings: latest.warnings,
    warnedRows: latest.warnedRows,
  };
}

/**
 * The list's snackbar for a finished run: a row refused (listed in the panel) or left warned makes it a warning;
 * rows that already had a value are reported but are not a failure (D17).
 */
export function bulkEditOutcome(
  summary: BulkEditSummary,
  t: (key: string, options?: Record<string, unknown>) => string,
): { severity: 'success' | 'warning'; message: string } {
  if (bulkRunIsPartial(summary)) {
    return { severity: 'warning', message: t('bulkEdit.partlyApplied', { applied: summary.applied, total: summary.total }) };
  }
  return summary.unchanged > 0
    ? { severity: 'success', message: t('bulkEdit.appliedWithUnchanged', { applied: summary.applied, unchanged: summary.unchanged }) }
    : { severity: 'success', message: t('bulkEdit.applied', { count: summary.applied }) };
}

/** A refused row's errors as the validation banner and panel show them, prefixed with the row. */
function refusalOf(row: BulkRowOutcome): ValidationError[] {
  const errors = row.error ? getEnvelopeValidationErrors(row.error) : [];
  const resolved = errors.length > 0
    ? errors
    : [{ field: 'form', message: row.error?.message ?? 'Refused', instancePath: '#/properties/form', members: ['form'] }];
  return resolved.map((error) => ({ ...error, message: `${row.label}: ${error.message}` }));
}
